import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus } from 'lucide-react';

import { ACCEPTABLE_IMAGES } from '@duke-hq/svc-files-lib/schemas';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { ImageUploadDisplay } from '@duke-hq/svc-files-ui/ImageUploadDisplay';
import { ContentGenerationLayout } from '@duke-hq/ui/components/ContentGeneration/ContentGenerationLayout';
import { ContentListItem } from '@duke-hq/ui/components/ContentGeneration/ContentListItem';
import {
  editAction,
  deleteAction,
} from '@duke-hq/ui/components/ContentGeneration/contentListItemActions';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { withPermission } from '../withPermission';
import { trpcAdmin, type RouterOutputs } from '../lib/trpcAdmin';
import { testIds } from './testIds';

type ContentPersona = RouterOutputs['contentPersonas']['all'][number];

interface PersonaFormData {
  title: string;
  name: string;
  description: string;
  emotionalRegister: string;
  avatarFileDrn: string | null;
}

export const ContentPersonasPage = withPermission('canManageCourseContent', () => {
  const toast = useToast();
  const drawer = useDisclosure();
  const deleteDialog = useDisclosure();

  const [selectedPersona, setSelectedPersona] = useState<ContentPersona | null>(null);
  const [personaToDelete, setPersonaToDelete] = useState<ContentPersona | null>(null);

  const personasQuery = trpcAdmin.contentPersonas.all.useQuery();

  const createMutation = trpcAdmin.contentPersonas.create.useMutation({
    onSuccess: () => {
      toast({ title: t`Persona created`, status: 'success', duration: 3000 });
      void personasQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to create persona`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const updateMutation = trpcAdmin.contentPersonas.update.useMutation({
    onSuccess: () => {
      toast({ title: t`Persona updated`, status: 'success', duration: 3000 });
      void personasQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to update persona`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const archiveMutation = trpcAdmin.contentPersonas.archive.useMutation({
    onSuccess: () => {
      toast({ title: t`Persona deleted`, status: 'success', duration: 3000 });
      void personasQuery.refetch();
      deleteDialog.onClose();
      setPersonaToDelete(null);
    },
    onError: (error) => {
      toast({
        title: t`Failed to delete persona`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  function handleCloseDrawer() {
    drawer.onClose();
    setSelectedPersona(null);
  }

  function handleOpenCreate() {
    setSelectedPersona(null);
    drawer.onOpen();
  }

  function handleOpenEdit(persona: ContentPersona) {
    setSelectedPersona(persona);
    drawer.onOpen();
  }

  function handleOpenDelete(persona: ContentPersona) {
    setPersonaToDelete(persona);
    deleteDialog.onOpen();
  }

  function handleConfirmDelete() {
    if (personaToDelete) {
      archiveMutation.mutate(personaToDelete.id);
    }
  }

  function handleSubmit(data: PersonaFormData) {
    if (selectedPersona) {
      updateMutation.mutate({ id: selectedPersona.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const personas = personasQuery.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <ContentGenerationLayout
        breadcrumb={<Trans>Unit Generation</Trans>}
        title={<Trans>Content Personas</Trans>}
        actions={
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            size="sm"
            onClick={handleOpenCreate}
            data-testid={testIds.addButton}
          >
            <Trans>Add new persona</Trans>
          </Button>
        }
        drawer={
          drawer.isOpen
            ? {
                isOpen: true,
                onClose: handleCloseDrawer,
                title: <Trans>Persona Settings</Trans>,
                body: (
                  <PersonaForm
                    persona={selectedPersona}
                    onSubmit={handleSubmit}
                    isSaving={isSaving}
                  />
                ),
              }
            : undefined
        }
      >
        {personasQuery.isPending ? (
          <Spinner data-testid={testIds.loadingSpinner} />
        ) : personas.length === 0 ? (
          <Text color="fg.muted" data-testid={testIds.emptyState}>
            <Trans>No content personas yet. Add a new persona to get started.</Trans>
          </Text>
        ) : (
          <ListCard
            cardTitle={<Trans>Personas</Trans>}
            cardSubtitle={
              <Trans>Character profiles to be utilised in the Duke Content Creation Pipeline</Trans>
            }
            cardContent={
              <VStack spacing="2" data-testid={testIds.personasList}>
                {personas.map((persona) => (
                  <ContentListItem
                    key={persona.id}
                    icon={
                      <Avatar size="sm" name={persona.name} src={persona.avatarUrl ?? undefined} />
                    }
                    title={persona.name}
                    description={persona.title}
                    onClick={() => handleOpenEdit(persona)}
                    menuActions={[
                      editAction(() => handleOpenEdit(persona)),
                      deleteAction(() => handleOpenDelete(persona)),
                    ]}
                  />
                ))}
              </VStack>
            }
          />
        )}
      </ContentGenerationLayout>

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        title={<Trans>Delete Persona</Trans>}
        body={
          <Text data-testid={testIds.deleteDialog}>
            <Trans>
              Are you sure you want to delete &quot;{personaToDelete?.title}&quot;? This action
              cannot be undone.
            </Trans>
          </Text>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={archiveMutation.isPending}
      />
    </>
  );
});

// -- Persona form -----------------------------------------------------

interface PersonaFormProps {
  persona: ContentPersona | null;
  onSubmit: (data: PersonaFormData) => void;
  isSaving: boolean;
}

function PersonaForm({ persona, onSubmit, isSaving }: PersonaFormProps) {
  const [title, setTitle] = useState(persona?.title ?? '');
  const [name, setName] = useState(persona?.name ?? '');
  const [description, setDescription] = useState(persona?.description ?? '');
  const [emotionalRegister, setEmotionalRegister] = useState(persona?.emotionalRegister ?? '');
  const [avatarFileDrn, setAvatarFileDrn] = useState(persona?.avatarFileDrn ?? null);

  const isValid =
    title.trim().length > 0 &&
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    emotionalRegister.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isValid) {
      onSubmit({
        title: title.trim(),
        name: name.trim(),
        description: description.trim(),
        emotionalRegister: emotionalRegister.trim(),
        avatarFileDrn,
      });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="5">
        <FileUploadContextDirect
          quantity="single"
          accept={ACCEPTABLE_IMAGES}
          attachedToKind="contentPersona"
          visibility="public"
          onFileComplete={(output) => setAvatarFileDrn(output.drn)}
        >
          <FileUploadTrigger>
            <Box
              mx="auto"
              w="160px"
              h="160px"
              cursor="pointer"
              userSelect="none"
              rounded="full"
              overflow="hidden"
              bg="bg.subtle"
              borderWidth="2px"
              borderColor="gray.300"
            >
              <ImageUploadDisplay
                imageUrl={persona?.avatarUrl ?? undefined}
                label={t`Upload avatar`}
              />
            </Box>
          </FileUploadTrigger>
        </FileUploadContextDirect>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Name</Trans>
          </FormLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t`e.g. Elena`}
            maxLength={255}
            data-testid={testIds.nameInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t`e.g. The Nurturing Connector`}
            maxLength={255}
            data-testid={testIds.titleInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Description</Trans>
          </FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t`Describe this persona's personality...`}
            maxLength={1000}
            rows={4}
            data-testid={testIds.descriptionInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Emotional Register</Trans>
          </FormLabel>
          <Textarea
            value={emotionalRegister}
            onChange={(e) => setEmotionalRegister(e.target.value)}
            placeholder={t`Describe the emotional register...`}
            maxLength={1000}
            rows={3}
            data-testid={testIds.emotionalRegisterInput}
          />
        </FormControl>

        <Button
          type="submit"
          colorScheme="brand"
          isLoading={isSaving}
          isDisabled={!isValid}
          data-testid={testIds.saveButton}
        >
          {persona ? <Trans>Update</Trans> : <Trans>Save</Trans>}
        </Button>
      </Stack>
    </form>
  );
}
